import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  HostListener,
  ViewChild,
  ElementRef,
  effect,
  inject,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';

import { environment } from '../environments/environment';
import { AuthService } from '@core/services/auth.service';
import { BannerService } from '@core/services/banner.service';
import { BannerSocketService } from '@core/services/socket.service';
import { IBanner } from '@core/interfaces/ibanner';
import { ILoginResponse } from '@core/interfaces/ilogin-response';

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  title = 'prism-client';

  private router = inject(Router);
  authService = inject(AuthService);
  private bannerService = inject(BannerService);
  private bannerSocket = inject(BannerSocketService);

  private subs = new Subscription();
  private recaptchaObserver?: MutationObserver;

  private user$ = toObservable<ILoginResponse | null>(this.authService.currentUserInfo);

  isLoggedIn$ = this.user$.pipe(
    map(u => !!u),
    startWith(this.authService.checkLogin())
  );

  banner$ = this.bannerSocket.banner$;

  @ViewChild('marqueeContent') marqueeContent?: ElementRef<HTMLElement>;
  @ViewChild('sessionExpiringDialog') sessionExpiringDialog?: ElementRef<HTMLElement>;
  @ViewChild('sessionTimeoutDialog') sessionTimeoutDialog?: ElementRef<HTMLElement>;

  private sessionExpiringPreviousFocus: HTMLElement | null = null;
  private sessionTimeoutPreviousFocus: HTMLElement | null = null;

  constructor() {
    effect(() => {
      const visible = this.authService.sessionExpiringVisible();
      if (visible) {
        this.sessionExpiringPreviousFocus = document.activeElement as HTMLElement;
        this.lockBodyScroll(true);
        setTimeout(() => this.sessionExpiringDialog?.nativeElement.focus());
      } else if (this.sessionExpiringPreviousFocus) {
        this.lockBodyScroll(this.authService.sessionTimeoutVisible());
        const target = this.sessionExpiringPreviousFocus;
        this.sessionExpiringPreviousFocus = null;
        setTimeout(() => target.focus());
      }
    });

    effect(() => {
      const visible = this.authService.sessionTimeoutVisible();
      if (visible) {
        this.sessionTimeoutPreviousFocus = document.activeElement as HTMLElement;
        this.lockBodyScroll(true);
        setTimeout(() => this.sessionTimeoutDialog?.nativeElement.focus());
      } else if (this.sessionTimeoutPreviousFocus) {
        this.lockBodyScroll(this.authService.sessionExpiringVisible());
        const target = this.sessionTimeoutPreviousFocus;
        this.sessionTimeoutPreviousFocus = null;
        setTimeout(() => target.focus());
      }
    });
  }

  // SCSS variables for animation
  marqueeDuration = '60s'; // computed each time; default is harmless
  marqueeDelay = '0s';     // negative delay so text is visible immediately
  startX = 0;              // px; start off the right edge
  endX = 0;                // px; end off the left edge

  private readonly speedPerSec = 60; // lower = slower, higher = faster
  private readonly minSeconds = 20;          // never faster than this
  private readonly maxSeconds = 180;         // never slower than this
  private readonly initialPeek = 48;  // how far inside from right at t=0

  ngOnInit(): void {
    if (environment.isSiteOffline) {
      this.authService.logout(true, false, true);
      // (isUnauthorized = true, isIdleTimeout = false, silent = true)
    }
    // load Google Analytics if tracking ID is configured
    if (environment.gaTrackingId) {
      this.loadGoogleAnalytics(environment.gaTrackingId);
    }

    // If already logged in at app start: start socket + seed + session monitor
    if (this.authService.checkLogin()) {
      this.bannerSocket.start();
      this.subs.add(
        this.bannerService.getBanner().subscribe(b => this.bannerSocket.seed(b))
      );
      this.authService.startSessionMonitor();
    }

    this.subs.add(
      this.user$.subscribe(user => {
        if (user) {
          this.bannerSocket.start();
          this.subs.add(
            this.bannerService.getBanner().subscribe(b => this.bannerSocket.seed(b))
          );
        } else {
          this.bannerSocket.stop();
          this.bannerSocket.seed({ message: '', isActive: false } as IBanner);
        }
        requestAnimationFrame(() => this.updateMarqueeMetrics());
      })
    );

    this.subs.add(
      this.router.events
        .pipe(filter(e => e instanceof NavigationEnd))
        .subscribe(() => {
          const mainContent = document.getElementById('main-content');
          if (mainContent) setTimeout(() => mainContent.focus(), 50);
        })
    );
  }

  ngAfterViewInit(): void {
    // Recompute marquee metrics whenever the live banner changes
    this.subs.add(
      this.banner$.subscribe(b => {
        if (b?.isActive && b.message?.trim()) {
          requestAnimationFrame(() => this.updateMarqueeMetrics());
        }
      })
    );

    this.patchExternalMarkup();
    this.recaptchaObserver = new MutationObserver(() => this.patchExternalMarkup());
    this.recaptchaObserver.observe(document.body, { childList: true, subtree: true });
  }

  private patchExternalMarkup(): void {
    this.labelRecaptchaTextareas();
    this.patchNotificationCloseButtons();
  }

  private patchNotificationCloseButtons(): void {
    document
      .querySelectorAll<HTMLElement>('.ant-notification-notice-close:not([data-prism-patched])')
      .forEach((el) => {
        if (!el.getAttribute('aria-label')) {
          el.setAttribute('aria-label', 'Close notification');
        }
        if (!el.hasAttribute('role')) {
          el.setAttribute('role', 'button');
        }
        el.setAttribute('data-prism-patched', 'true');
      });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.bannerSocket.stop();
    this.recaptchaObserver?.disconnect();
  }

  private labelRecaptchaTextareas(): void {
    document.querySelectorAll<HTMLTextAreaElement>('textarea.g-recaptcha-response')
      .forEach(el => {
        if (!el.hasAttribute('aria-label')) {
          el.setAttribute('aria-label', 'reCAPTCHA verification token');
          el.setAttribute('title', 'reCAPTCHA verification token');
        }
        if (!el.hasAttribute('inert')) {
          el.setAttribute('inert', '');
          el.removeAttribute('aria-hidden');
          el.removeAttribute('tabindex');
        }
      });

    document.querySelectorAll<HTMLElement>('.grecaptcha-badge, .g-recaptcha, iframe[src*="recaptcha"], iframe[title*="recaptcha" i]')
      .forEach(el => {
        if (!el.hasAttribute('inert')) {
          el.setAttribute('inert', '');
          el.removeAttribute('aria-hidden');
          el.removeAttribute('tabindex');
        }
      });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateMarqueeMetrics();
  }

  private updateMarqueeMetrics(): void {
    const contentEl = this.marqueeContent?.nativeElement;
    const containerEl = contentEl?.parentElement as HTMLElement | undefined;
    if (!contentEl || !containerEl) return;

    const contentWidth = Math.ceil(contentEl.scrollWidth);
    const containerWidth = Math.ceil(containerEl.clientWidth);
    if (!contentWidth || !containerWidth) return;

    // Start just off the right; end off the left
    this.startX = containerWidth;
    this.endX = -contentWidth;

    const distance = this.startX - this.endX;

    // Constant perceived speed
    const speed = this.speedPerSec;
    const seconds = Math.min(this.maxSeconds, Math.max(this.minSeconds, distance / speed));
    this.marqueeDuration = `${seconds}s`;

    // Negative delay so it's visible immediately at initialPeek inside the right edge
    const enterOffsetPx = Math.min(this.initialPeek, containerWidth);
    const tEnter = enterOffsetPx / speed;
    this.marqueeDelay = `-${tEnter.toFixed(3)}s`;
  }

  private loadGoogleAnalytics(trackingId: string): void {
    // load gtag script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    document.head.appendChild(script1);

    // initialize gtag
    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${trackingId}');
    `;
    document.head.appendChild(script2);
  }

  skipToContent(event: MouseEvent): void {
    event.preventDefault();
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
      mainContent.scrollIntoView({ behavior: 'smooth' });
    }
  }

  onSessionExpiringKeydown(event: KeyboardEvent, dialog: HTMLElement): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.authService.confirmSessionExtension();
      return;
    }
    this.trapTab(event, dialog);
  }

  onSessionTimeoutKeydown(event: KeyboardEvent, dialog: HTMLElement): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.authService.acknowledgeSessionTimeout();
      return;
    }
    this.trapTab(event, dialog);
  }

  private trapTab(event: KeyboardEvent, dialog: HTMLElement): void {
    if (event.key !== 'Tab') return;
    const focusables = this.getFocusableElements(dialog);
    if (!focusables.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden')
    );
  }

  private lockBodyScroll(lock: boolean): void {
    document.body.style.overflow = lock ? 'hidden' : '';
  }
}
